/**
 * ping-pong-goldfish.js — GDD 6.3.2.
 *
 * Six bowls on a table and five ping pong balls. Land one and you win the fish
 * that goes with that bowl's size: large bowl 50 pts and a common orange,
 * medium 150 and a spotted, small 500 and a rare golden. Every landing wins a
 * fish (GDD 7.4) — there is no threshold to clear.
 *
 * The rule that makes it a game rather than a lob: **the ball must touch the
 * table before it enters a bowl**. A direct throw is refused — the ball is
 * popped straight back out and scores nothing. Table contact is tracked per
 * ball from Rapier collision events, so it is the real first bounce that
 * counts, not a proxy for one.
 *
 * Bowls are trimesh shells rather than the factory's solid cylinder: a solid
 * collider would make the mouth of a bowl the one place a ball can never
 * reach, where a shell lets it drop inside and rattle against the glass.
 *
 * @module stalls/ping-pong-goldfish
 */

import * as THREE from 'three';
import { PING_PONG_GOLDFISH, PRIZE_KIND, PALETTE, HUB, EVENTS } from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import * as factory from '../assets3d/factory.js';
import { createStallLight } from '../world/lighting.js';
import { bus } from '../core/events.js';
import { audio } from '../audio/index.js';
import { haptics } from '../core/haptics.js';

const CFG = PING_PONG_GOLDFISH;

/** Trimesh data is identical for every bowl of a size — build each one once. */
const BOWL_SHELLS = new Map();

/**
 * An open-topped bowl shell for Rapier, matching the factory's visual bowl.
 * Rapier trimeshes are zero-thickness surfaces with no inside or outside, so a
 * ball can sit inside one and still collide with the walls.
 *
 * @param {number} r Bowl radius.
 * @returns {{ vertices: Float32Array, indices: Uint32Array }}
 */
function bowlShell(r) {
  const cached = BOWL_SHELLS.get(r);
  if (cached) return cached;
  // Coarse on purpose. Six bowls' worth of trimesh is the most expensive thing
  // in this stall, and a ping pong ball cannot resolve facets this small.
  const g = new THREE.SphereGeometry(r, 10, 7, 0, Math.PI * 2, Math.PI * 0.32, Math.PI * 0.68);
  g.translate(0, r, 0);   // the factory seats the sphere at y = r above the base
  const shell = {
    vertices: new Float32Array(g.attributes.position.array),
    indices: new Uint32Array(g.index.array),
  };
  g.dispose();
  BOWL_SHELLS.set(r, shell);
  return shell;
}

class PingPongGoldfish extends StallBase {
  constructor(ctx) {
    super(ctx);
    /**
     * @type {Array<{ id: string, row: object, local: THREE.Vector3, mesh: THREE.Object3D,
     *                handle: object, radius: number, mouthRadius: number, rimY: number,
     *                landings: number }>}
     */
    this.bowls = [];
    /**
     * @type {Array<{ handle: object, bounced: boolean, captured: object|null,
     *                restTimer: number, cooldown: number, age: number,
     *                scored: boolean, resolved: boolean }>}
     */
    this.balls = [];
    /** @type {Array<(dt: number) => boolean>} Splashes and swimming prize fish. */
    this._effects = [];
    this._settleTimer = 0;
    this._popped = 0;
    this._offCollision = null;
  }

  // ── Static build ────────────────────────────────────────────────────────

  onBuild() {
    this._buildFloor();
    this._buildTable();

    // Small price cards, standing on the table BESIDE each row. Centred in
    // front of a row a card is a wall: at any legible size it is several times
    // wider than a bowl and hides everything behind it.
    for (const row of CFG.rows) {
      const label = factory.createSign('', {
        w: 0.34, h: 0.11,
        lines: [`${row.points} PTS`],
        bg: '#12172a',
      });
      label.position.set(-0.86, CFG.tableHeight + 0.06, row.z);
      label.rotation.y = Math.PI;
      this.root.add(label);
    }

    this.buildTray({ width: 0.72, y: 1.20, z: 0.72 });
    this.buildScoreboard({ y: 2.3, z: 3.5 });

    const light = createStallLight(0xfff0d8, 2.0, 11);
    light.position.set(0, 3.0, 2.4);
    this.root.add(light);

    // Table contacts are what make the bounce rule real. Rapier reports a
    // collision when EITHER collider opts in, so flagging the balls is enough
    // to hear about every surface they touch.
    this._offCollision = bus.on(EVENTS.COLLISION, (e) => this._onCollision(e));
  }

  /**
   * A fixed box, collider centred on the mesh. `spawn()` leaves a fixed mesh at
   * its local origin, so the body position has to be given explicitly.
   * @private
   */
  _slab(color, size, at, opts = {}) {
    const m = new THREE.Mesh(
      new THREE.BoxGeometry(size[0], size[1], size[2]),
      factory.mat(color, { rough: opts.rough ?? 0.9 }),
    );
    m.receiveShadow = true;
    return this.spawn(m, { x: at[0], y: at[1], z: at[2] }, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: size[0] / 2, y: size[1] / 2, z: size[2] / 2 },
      position: this.localToWorld(at[0], at[1], at[2]),
      friction: opts.friction ?? 0.9, restitution: opts.restitution ?? 0.2,
      userData: opts.userData,
    });
  }

  /** @private */
  _buildFloor() {
    const w = HUB.boothWidth - 0.4;
    this._slab(0x4f6b5a, [w, 0.1, 6.4], [0, -0.05, 2.6], { rough: 1 });
    // Light enough to read as a backdrop rather than a hole in the booth.
    this._slab(0x9c8fae, [w, 2.2, 0.1], [0, 1.1, 4.4], { rough: 0.95, restitution: 0.1 });
  }

  /** @private */
  _buildTable() {
    this._slab(
      0x2f6f4f,
      [CFG.tableWidth, 0.08, CFG.tableHalfDepth * 2],
      [0, CFG.tableHeight - 0.04, CFG.tableZ],
      {
        rough: 0.75, friction: CFG.tableFriction, restitution: CFG.tableRestitution,
        userData: { table: true },
      },
    );

    const trim = new THREE.Mesh(
      new THREE.BoxGeometry(CFG.tableWidth + 0.04, 0.1, 0.05),
      factory.mat(PALETTE.boothFrame, { rough: 0.9 }),
    );
    trim.position.set(0, CFG.tableHeight - 0.05, CFG.tableZ - CFG.tableHalfDepth);
    this.root.add(trim);
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    this.setRoundFormat({ attempts: CFG.ballsPerRound });
    this.bowls = [];
    this.balls = [];
    this._effects = [];
    this._settleTimer = 0;
    this._popped = 0;

    for (const row of CFG.rows) this._buildRow(row);

    const handles = this.dispense(CFG.ballsPerRound, () => factory.createBall('pingpong'), {
      y: 1.24, z: 0.72, spacing: 0.09,
      desc: {
        restitution: CFG.ballRestitution,
        gravityScale: CFG.ballGravityScale,
        ccd: true,
        events: true,
        angularDamping: 0.02,
        userData: { pingpong: true },
      },
    });
    for (const handle of handles) {
      this.balls.push({
        handle, bounced: false, wetted: false, captured: null, restTimer: 0,
        cooldown: 0, age: 0, scored: false, resolved: false,
      });
    }
  }

  /**
   * @param {{size: string, z: number, xs: number[], points: number, variant: string}} row
   * @private
   */
  _buildRow(row) {
    for (let i = 0; i < row.xs.length; i++) {
      const x = row.xs[i];
      const local = new THREE.Vector3(x, CFG.tableHeight, row.z);
      const mesh = factory.createBowl(row.size);
      // Measure the bowl rather than back it out of `mouthRadius`. The shell
      // has to match the glass the player sees, and the factory owns both the
      // radius and the ratio between them — measuring survives either changing.
      mesh.updateMatrixWorld(true);
      const radius = new THREE.Box3().setFromObject(mesh).getSize(new THREE.Vector3()).x / 2;

      const handle = this.spawn(mesh, local, {
        type: 'fixed', shape: 'trimesh',
        ...bowlShell(radius),
        position: this.localToWorld(x, CFG.tableHeight, row.z),
        friction: 0.25, restitution: 0.45,
        userData: { bowl: row.size },
      });

      const id = `${row.size}-${i}`;
      this.registerTarget(handle, { id, group: row.size });
      this.bowls.push({
        id, row, local, mesh, handle, radius,
        mouthRadius: mesh.userData.mouthRadius,
        rimY: mesh.userData.rimY,
        landings: 0,
      });
    }
  }

  // ── Collision events ────────────────────────────────────────────────────

  /**
   * Record the first table contact for a ball. This is the entire bounce rule
   * (GDD 6.3.2) — a ball that has never been here cannot score.
   * @private
   */
  _onCollision({ a, b, started }) {
    if (!started) return;
    const ball = a?.userData.pingpong ? a : (b?.userData.pingpong ? b : null);
    if (!ball) return;
    const other = ball === a ? b : a;
    if (!other?.userData.table) return;

    const entry = this.balls.find((e) => e.handle === ball);
    if (!entry || entry.bounced) return;
    entry.bounced = true;
    audio.play('impact-miss', { volume: 0.3 });
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  onUpdate(dt) {
    this._trackBalls(dt);
    this._runEffects(dt);
    this._checkRoundEnd(dt);
  }

  /** @private */
  _trackBalls(dt) {
    for (const ball of this.balls) {
      if (ball.resolved) continue;
      const h = ball.handle;
      if (h.removed) { ball.resolved = true; continue; }
      if (!h.userData.thrown) continue;

      ball.age += dt;
      ball.cooldown = Math.max(0, ball.cooldown - dt);

      const local = this.worldToLocal(h.position());
      const bowl = ball.cooldown > 0 ? null : this._bowlContaining(local);

      if (bowl && !ball.bounced) {
        this._popOut(ball, bowl);
        continue;
      }

      // Breaking the water kills almost all of the ball's energy, so a legal
      // landing settles instead of pinging back out of the glass.
      if (bowl && !ball.wetted) {
        ball.wetted = true;
        h.setVelocity(h.velocity().multiplyScalar(CFG.waterDamping));
        h.setAngularVelocity({ x: 0, y: 0, z: 0 });
      }

      ball.captured = bowl;
      const slow = h.velocity().lengthSq() < CFG.restSpeed * CFG.restSpeed;
      ball.restTimer = slow ? ball.restTimer + dt : 0;

      if (bowl && ball.restTimer >= CFG.restTime) {
        this._onBallLanded(ball, bowl);
      } else if (!bowl && (ball.restTimer >= CFG.restTime || ball.age >= CFG.ballLifetime)) {
        // Came to rest on the table or the floor — a miss, but it still has to
        // be marked done or the round never closes.
        ball.resolved = true;
        h.freeze();
      }
    }
  }

  /**
   * @param {THREE.Vector3} local Ball centre in stall-local space.
   * @returns {object|null} The bowl whose mouth the ball is inside.
   * @private
   */
  _bowlContaining(local) {
    for (const b of this.bowls) {
      const dx = local.x - b.local.x;
      const dz = local.z - b.local.z;
      const rel = local.y - b.local.y;
      // Inside the glass sphere, not merely within the bowl's footprint. The
      // bowl bulges out over the table, and a ball that rolled under that
      // overhang is beside the bowl — a footprint test would score it.
      const dy = rel - b.radius;
      if (dx * dx + dy * dy + dz * dz > b.radius * b.radius) continue;
      // And it has to be below the rim, i.e. actually in (GDD 6.3.2).
      if (rel > b.rimY) continue;
      return b;
    }
    return null;
  }

  /**
   * Refuse a direct throw. GDD 6.3.2: "direct throws bounce out".
   * @private
   */
  _popOut(ball, bowl) {
    const h = ball.handle;
    const local = this.worldToLocal(h.position());
    const out = new THREE.Vector3(local.x - bowl.local.x, 0, local.z - bowl.local.z);
    if (out.lengthSq() < 1e-6) out.set(0, 0, -1);
    out.normalize().multiplyScalar(CFG.popOutLateral);
    out.y = CFG.popOutUp;

    h.setVelocity(this.localToWorldDirection(out));
    ball.cooldown = CFG.popOutCooldown;
    ball.restTimer = 0;
    this._popped += 1;
    audio.play('impact-miss', { volume: 0.5 });
    haptics.play('right', 'miss');
  }

  /** @private */
  _onBallLanded(ball, bowl) {
    ball.scored = true;
    ball.resolved = true;
    bowl.landings += 1;
    bowl.handle.userData.scored = true;

    // Park it in the bowl so the next ball cannot knock a won fish loose.
    const h = ball.handle;
    h.setKinematic(true);
    h.freeze();

    const world = h.position();
    this.addScore(bowl.row.points, {
      position: world,
      label: `${bowl.row.size} bowl`,
      color: bowl.row.size === 'small' ? '#ffd700' : '#6fe7c4',
    });

    // GDD 7.4: every landing wins its fish. The name is left unset — the
    // personal-space agent's naming UI listens for DIRECT_PRIZE and fills it.
    this.awardDirectPrize(PRIZE_KIND.FISH, { variant: bowl.row.variant });

    this._splash(bowl);
    this._addFish(bowl);
    audio.play('prize-earned');
    haptics.play('right', 'score');
    this.updateScoreboard();
  }

  /**
   * Water splash on entry (GDD 6.3.2). Decor in `this.root`, so teardown takes
   * it; the material is per-splash because its opacity animates.
   * @private
   */
  _splash(bowl) {
    const material = new THREE.MeshBasicMaterial({
      color: 0xbfe9ff, transparent: true, opacity: 0.85, side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(bowl.mouthRadius * 0.35, bowl.mouthRadius * 0.5, 20),
      material,
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(bowl.local.x, bowl.local.y + bowl.rimY * 0.8, bowl.local.z);
    this.root.add(ring);

    let t = 0;
    this._effects.push((dt) => {
      t += dt;
      const k = t / CFG.splashTime;
      ring.scale.setScalar(1 + k * (CFG.splashRadius - 1));
      material.opacity = Math.max(0, 0.85 * (1 - k));
      if (k < 1) return true;
      this.root.remove(ring);
      ring.geometry.dispose();
      material.dispose();
      return false;
    });
  }

  /** The prize fish, circling the bowl it was won from. @private */
  _addFish(bowl) {
    const fish = factory.createFish(bowl.row.variant);
    fish.scale.setScalar(bowl.radius / 0.13);
    this.root.add(fish);
    const swim = bowl.radius * 0.35;
    let phase = bowl.landings * 1.7;
    this._effects.push((dt) => {
      phase += Math.min(dt, 0.1) * 1.6;
      fish.position.set(
        bowl.local.x + Math.cos(phase) * swim,
        bowl.local.y + bowl.radius * 0.6,
        bowl.local.z + Math.sin(phase * 0.8) * swim,
      );
      fish.rotation.y = -phase;
      return true;      // swims until teardown empties the effect list
    });
  }

  /** @private */
  _runEffects(dt) {
    if (this._effects.length === 0) return;
    this._effects = this._effects.filter((fn) => fn(dt));
  }

  /** @private */
  _checkRoundEnd(dt) {
    if (!this.roundActive || this.attemptsLeft > 0) return;
    if (this.balls.every((b) => b.resolved)) {
      this.endRound();
      return;
    }
    this._settleTimer += dt;
    if (this._settleTimer >= CFG.settleTime) this.endRound();
  }

  // ── Aim assist ──────────────────────────────────────────────────────────

  /**
   * Assist toward the BOUNCE POINT in front of the nearest bowl, not the bowl
   * itself. Pulling throws straight at a bowl would fight the one rule the
   * stall is built on (GDD 5.4 gives it a narrow 5° cone for the same reason).
   */
  findAimTarget(releasePos) {
    let best = null;
    let bestD = Infinity;
    for (const b of this.bowls) {
      const p = this.localToWorld(b.local.x, CFG.tableHeight, b.local.z - 0.35);
      const d = p.distanceTo(releasePos);
      if (d < bestD) { bestD = d; best = p; }
    }
    return best ? { position: best } : null;
  }

  // ── Test hooks ──────────────────────────────────────────────────────────

  /** Keep a relocated bowl's mesh with its trimesh body. See RingToss. */
  moveTarget(targetId, localPos, rotation) {
    if (!super.moveTarget(targetId, localPos, rotation)) return false;
    const b = this.bowls.find((e) => e.id === targetId);
    if (!b) return true;
    b.local.set(localPos[0], localPos[1], localPos[2]);
    b.mesh.position.copy(b.local);
    return true;
  }

  /** Diagnostic state for `__cc.stalls.debug()`. */
  debugState() {
    return {
      stallId: this.id,
      poppedOut: this._popped,
      bowls: this.bowls.map((b) => ({
        id: b.id, size: b.row.size, variant: b.row.variant,
        landings: b.landings, local: b.local.toArray(),
      })),
      balls: this.balls.map((b) => ({
        thrown: !!b.handle.userData.thrown,
        bounced: b.bounced,
        scored: b.scored,
        resolved: b.resolved,
        inBowl: b.captured ? b.captured.id : null,
      })),
    };
  }

  // ── Scoreboard ──────────────────────────────────────────────────────────

  updateScoreboard() {
    if (!this.scoreboard) return;
    const total = this.bowls.reduce((n, b) => n + b.landings, 0);
    const bySize = (size) => this.bowls
      .filter((b) => b.row.size === size)
      .reduce((n, b) => n + b.landings, 0);
    this.scoreboard.setLines([
      `Score   ${this.score}`,
      `Balls   ${this.attemptsLeft} / ${this.maxAttempts}`,
      `Fish    ${total}`,
      `L ${bySize('large')}   M ${bySize('medium')}   S ${bySize('small')}`,
    ]);
  }

  onTeardown() {
    if (this._offCollision) this._offCollision();
    this._offCollision = null;
    for (const fn of this._effects) fn(Infinity);   // let splashes free their materials
    this._effects = [];
    this.bowls = [];
    this.balls = [];
  }
}

registerStall({
  id: 'ping-pong-goldfish',
  name: 'Ping Pong Goldfish',
  cost: CFG.cost,
  create: (ctx) => new PingPongGoldfish(ctx),
});

export { PingPongGoldfish };
